import type { CosmosQueryResponse } from "@/lib/cosmos-api";
import {
	resolveRowDescription,
	resolveRowDurationMinutes,
	resolveRowStringField,
	resolveRowTitle,
} from "@/lib/format";

type ConversationExecutionEntity = {
	id?: string;
	title: string;
	entityType?: string;
	description?: string;
	durationMinutes?: number;
	format?: string;
	status?: string;
	metadata?: Record<string, unknown>;
};

export type ClientConversationExecutionSnapshot = {
	planId: string;
	intent: string;
	domains: string[];
	topicTerms?: string[];
	entities: ConversationExecutionEntity[];
};

export type ClientConversationTurn = {
	role: "user" | "assistant";
	content: string;
	executionSnapshot?: ClientConversationExecutionSnapshot;
};

export const buildExecutionSnapshotFromQueryResult = (
	queryResult: CosmosQueryResponse,
): ClientConversationExecutionSnapshot | undefined => {
	const queryPlan = queryResult.queryPlan;
	const planId =
		(queryPlan as { planId?: string } | undefined)?.planId ??
		queryResult.intentId ??
		"client-snapshot";
	const entities = queryResult.rows
		.map((row) => {
			const title = resolveRowTitle(row);
			if (title === "Result") {
				return undefined;
			}
			const id = resolveRowStringField(row, ["id"]);
			const description = resolveRowDescription(row);
			const durationMinutes = resolveRowDurationMinutes(row);
			const entityType =
				typeof row.entityType === "string"
					? row.entityType
					: queryPlan.domains?.[0];
			return {
				title,
				...(id ? { id } : {}),
				...(entityType ? { entityType } : {}),
				...(description ? { description } : {}),
				...(durationMinutes !== undefined ? { durationMinutes } : {}),
			};
		})
		.filter((entity): entity is ConversationExecutionEntity => Boolean(entity))
		.slice(0, 20);
	if (entities.length === 0) {
		return undefined;
	}
	return {
		planId,
		intent: queryPlan?.intent ?? "list",
		domains: queryPlan?.domains ?? ["course"],
		topicTerms: (
			queryPlan as
				| { filters?: Array<{ field?: string; value?: unknown }> }
				| undefined
		)?.filters
			?.filter(
				(filter) => filter.field === "topic" && typeof filter.value === "string",
			)
			.map((filter) => String(filter.value)),
		entities,
	};
};

export const buildClientConversationHistory = (
	messages: Array<{
		role: "user" | "assistant";
		content: string;
		status?: "thinking" | "complete" | "error";
		executionSnapshot?: ClientConversationExecutionSnapshot;
		queryResult?: CosmosQueryResponse | null;
	}>,
): ClientConversationTurn[] =>
	messages
		.filter((message) => message.content.trim().length > 0 && message.status !== "thinking")
		.slice(-12)
		.map((message) => {
			const turn: ClientConversationTurn = {
				role: message.role,
				content: message.content,
			};
			const snapshot =
				message.executionSnapshot ??
				(message.role === "assistant" && message.queryResult
					? buildExecutionSnapshotFromQueryResult(message.queryResult)
					: undefined);
			if (snapshot?.entities.length) {
				turn.executionSnapshot = snapshot;
			}
			return turn;
		});
